import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Box, Container } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { api } from 'src/service';
import ClientsListResults from '../../components/clients/ClientsListResults';
import ClientsToolBar from '../../components/clients/ClientsToolBar';
import { getClientsList, removeClient, updateClientByID } from '../../redux/clients/action';
import { usePersistedPagerInfo } from 'src/utils/usePersistedLimit';

const Clients = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [clientID, setClientID] = useState();
  const [searchStr, setSearchStr] = useState('');
  const [pagerInfo, setPagerInfo] = usePersistedPagerInfo('clients', { limit: 10 });
  const [sortBy, setSortBy] = useState('creation_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        await api
          .post('/backoffice/clients/filter', {
            page_size: pagerInfo.limit,
            current_page: pagerInfo.page,
            search: searchStr,
            sort_by: sortBy,
            sort_order: sortOrder,
          })
          .then((res) => {
            setClients(res.data.data.data);
            setPagerInfo({
              limit: res.data.data.page_size,
              page: res.data.data.current_page,
              totalSize: res.data.data.total_size,
            });
          });
      } catch (error) {
        const message = error.response
          ? error.response.data.moreInformation.error
          : { message: error.message };
        throw new Error(message);
      }
      setLoading(false);
    };

    fetchData();
  }, [searchStr, pagerInfo.page, pagerInfo.limit, sortBy, sortOrder]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleRow = async (id) => {
    handleOpen(true);
    setClientID(id);
  };

  const handleRemove = async () => {
    setOpen(false);
    setLoading(true);
    await dispatch(removeClient(clientID));
    const data = await dispatch(getClientsList());
    setClients(data);
    setLoading(false);
  };

  const handleToggleActive = async (id, currentStatus) => {
    const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';
    const ok = window.confirm(`Set client to ${newStatus.toUpperCase()}?`);
    if (!ok) return;
    setLoading(true);
    try {
      await dispatch(updateClientByID({ status: newStatus }, id));
      const data = await dispatch(getClientsList());
      setClients(data);
    } catch (e) {
      window.alert('Failed to update status: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchStr = (str) => {
    setSearchStr(str);
  };

  const handlePagerInfo = (info) => {
    setPagerInfo(info);
  };

  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setPagerInfo((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <Helmet>
        <title>Washer - Clients</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3,
        }}
      >
        <Container maxWidth={false}>
          <ClientsToolBar handleSearchStr={handleSearchStr} />
          <Box sx={{ pt: 3 }}>
            <ClientsListResults
              clients={clients}
              handleRow={handleRow}
              handleToggleActive={handleToggleActive}
              loading={loading}
              pagerInfo={pagerInfo}
              handlePagerInfo={handlePagerInfo}
              onSortChange={handleSortChange}
            />
          </Box>
          <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle id="responsive-dialog-title">Confirmation</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to remove it?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={handleClose} color="primary">
                Disagree
              </Button>
              <Button onClick={handleRemove} color="primary" autoFocus>
                Agree
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </>
  );
};

export default Clients;
